<script setup lang="ts">
interface Props {
  content: string
  offset?: number
  maxWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  offset: 10,
  maxWidth: 520,
})

const isVisible = ref(false)
const left = ref(0)
const top = ref(0)
const tooltipRef = ref<HTMLElement | null>(null)
const activeTarget = ref<HTMLElement | null>(null)

const updatePosition = (target: HTMLElement | null = activeTarget.value) => {
  if (!target || !isVisible.value) {
    return
  }

  const rect = target.getBoundingClientRect()
  const tooltipWidth = tooltipRef.value?.offsetWidth ?? 240
  const tooltipHeight = tooltipRef.value?.offsetHeight ?? 0

  const nextLeft = rect.left + rect.width / 2 - tooltipWidth / 2
  const clampedLeft = Math.max(12, Math.min(window.innerWidth - tooltipWidth - 12, nextLeft))
  const nextTop = rect.top - tooltipHeight - props.offset

  left.value = clampedLeft
  top.value = Math.max(8, nextTop)
}

const show = (event: MouseEvent | FocusEvent) => {
  const target = event.currentTarget as HTMLElement | null
  if (!target || !props.content) {
    return
  }

  activeTarget.value = target
  isVisible.value = true

  nextTick(() => {
    updatePosition(target)
  })
}

const hide = () => {
  isVisible.value = false
  activeTarget.value = null
}

const onViewportChange = () => {
  updatePosition()
}

onMounted(() => {
  window.addEventListener('scroll', onViewportChange, true)
  window.addEventListener('resize', onViewportChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
})

const triggerProps = {
  onMouseenter: show,
  onMouseleave: hide,
  onFocus: show,
  onBlur: hide,
}

const tooltipStyle = computed(() => ({
  left: `${left.value}px`,
  maxWidth: `${props.maxWidth}px`,
  top: `${top.value}px`,
}))
</script>

<template>
  <slot :trigger-props="triggerProps" />

  <Teleport to="body">
    <div
      v-if="isVisible"
      ref="tooltipRef"
      class="project-files-item-tooltip"
      :style="tooltipStyle"
      role="tooltip"
    >
      {{ content }}
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.project-files-item-tooltip {
  position: fixed;
  z-index: 3000;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.2);
  font-size: 0.8rem;
  line-height: 1.3;
  white-space: pre-line;
  overflow-wrap: anywhere;
  pointer-events: none;
}
</style>
